import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import { worksList } from "@/mocks/works";

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = worksList.find((w) => w.id === id);

  if (!item) {
    return (
      <SiteLayout title="作品未找到 - 明星动态平台">
        <div className="px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-5">
            <i className="ri-film-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900">
            抱歉，未找到该作品
          </h1>
          <p className="text-sm text-foreground-500 mt-3 font-body">
            这部作品可能已下架，去看看其他作品吧。
          </p>
          <Link
            to="/works"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-body hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回作品列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = worksList
    .filter((w) => w.id !== item.id)
    .sort((a, b) => {
      const scoreA =
        (a.type === item.type ? 2 : 0) + (Math.abs(a.year - item.year) <= 1 ? 1 : 0);
      const scoreB =
        (b.type === item.type ? 2 : 0) + (Math.abs(b.year - item.year) <= 1 ? 1 : 0);
      if (scoreA !== scoreB) return scoreB - scoreA;
      return b.year - a.year;
    })
    .slice(0, 4);

  const metaRows = [
    { label: "作品类型", value: item.type },
    { label: "上映年份", value: `${item.year} 年` },
    { label: "导演", value: item.director },
    { label: "角色类型", value: item.roleType },
    { label: "饰演角色", value: item.role },
    { label: "作品规格", value: item.meta },
  ];

  return (
    <SiteLayout
      title={`${item.title}（${item.year}）- 白石茉莉奈作品 - 明星动态平台`}
      description={`${item.title}：${item.synopsis.slice(0, 60)}`}
      keywords={`白石茉莉奈,${item.title},${item.type},${item.year}年作品`}
    >
      <div className="px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "作品", to: "/works" },
            { label: item.type, to: `/works?type=${encodeURIComponent(item.type)}` },
            { label: item.title },
          ]}
        />
      </div>

      <div className="px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-1">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} - 白石茉莉奈作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 bg-background-100 rounded-lg py-3">
              <i className="ri-star-fill text-accent-500"></i>
              <span className="font-heading text-lg text-foreground-900">
                {item.rating.toFixed(1)}
              </span>
              <span className="text-sm text-foreground-400 font-body">/ 10.0</span>
            </div>
          </div>

          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 text-xs font-body px-3 py-1.5 rounded-full bg-accent-500 text-foreground-950">
              <i className="ri-award-line"></i>
              {item.roleType}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-4">
              {item.title}
            </h1>
            <p className="text-sm text-foreground-400 mt-2 font-body">
              {item.year} · {item.meta}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 mt-7 border-t border-background-200">
              {metaRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-start gap-4 py-4 border-b border-background-200"
                >
                  <span className="text-sm text-foreground-400 font-body w-20 shrink-0">
                    {row.label}
                  </span>
                  <span className="text-sm text-foreground-900 font-body">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-lg text-foreground-950 mt-8 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500"></span>
              作品简介
            </h2>
            <p className="text-base text-foreground-700 leading-[1.9] font-body">
              {item.synopsis}
            </p>

            <h2 className="font-heading text-lg text-foreground-950 mt-8 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500"></span>
              角色表现
            </h2>
            <p className="text-base text-foreground-700 leading-[1.9] font-body">
              白石茉莉奈在《{item.title}》中饰演{item.role}
              ，以细腻而克制的表演，将角色的内心层次层层铺陈。无论是情绪的收放还是细节的处理，
              都展现了她在{item.type}题材上的稳定功力，是这部作品的重要看点之一。
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/works?type=${encodeURIComponent(item.type)}`}
                className="text-sm font-body px-4 py-2 rounded-full bg-primary-100 text-primary-800 hover:bg-primary-500 hover:text-background-50 transition-colors"
              >
                {item.type}作品
              </Link>
              <Link
                to={`/works?year=${item.year}`}
                className="text-sm font-body px-4 py-2 rounded-full bg-secondary-100 text-secondary-800 hover:bg-secondary-500 hover:text-background-50 transition-colors"
              >
                {item.year} 年作品
              </Link>
              <Link
                to="/works"
                className="text-sm font-body px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors"
              >
                返回作品列表
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-6 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            相关作品推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((rel) => (
              <WorkCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}